import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React, {useCallback, useMemo} from 'react';

import Accordion from 'ui-library/Accordion';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {Cell, CellRenderOptions, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {Transition} from '../types/transition/TransitionTypes';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {buildTransitionDisplayInfos, TransitionDisplayInfo} from './flowchartTransitionUtils';
import SingleFlowchartTransitionModal, {
  TransitionModalAction,
  TransitionType,
} from './transition-modal/SingleFlowchartTransitionModal';

export const PLANNED_TRANSITIONS_EMPTY_STATE_MESSAGE =
  'Planned transitions will appear here after you draw your flowchart, and are included in every flowchart.';
export const OPTIONAL_TRANSITIONS_EMPTY_STATE_MESSAGE =
  'Create optional transitions that users can add when creating or modifying flowcharts.';

export type {TransitionDisplayInfo} from './flowchartTransitionUtils';

export interface FlowchartTransitionsSidepanelContentProps {
  metadataTransitions: Record<string, Transition>;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  /** Called when a transition is edited/saved from the modal */
  onEditTransition: (transitionId: string) => void;
  /** Called when a new optional transition is created from the modal */
  onCreateTransition: () => void;
  /** Called when an optional transition is deleted */
  onDeleteTransition: (transitionId: string) => void;
  /** Called when the user clicks "Go to flowchart" in the planned transitions empty state */
  onGoToFlowchart?: () => void;
  isLocked: boolean;
}

interface TransitionRowActionsProps {
  transitionId: string;
  isRowHovered: boolean;
  showDeleteButton: boolean;
  isLocked: boolean;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
}

function TransitionRowActions(props: TransitionRowActionsProps): JSX.Element {
  const {transitionId, isRowHovered, showDeleteButton, isLocked, onViewOrEditTransition, onDeleteTransition} =
    props;

  const editIcon = isLocked ? 'eye' : 'edit';
  const editTooltip = isLocked ? 'View transition' : 'Edit transition';

  return (
    <div className={css(styles.actionButtons, isRowHovered && styles.actionButtonsVisible)}>
      <TooltipWrapper tooltipTitle={editTooltip}>
        <Button
          buttonStyle={ButtonStyle.TRANSPARENT}
          size={ButtonSize.SMALL}
          icon={<BenchlingIcon icon={editIcon} className={css(styles.actionButtonIcon)} />}
          onClick={() => onViewOrEditTransition?.(transitionId)}
          className={css(styles.actionButton)}
          dataTestAttrs={{
            component: 'FlowchartTransitionsSidepanelContent',
            element: isLocked ? 'viewTransitionButton' : 'editTransitionButton',
            key: transitionId,
          }}
        />
      </TooltipWrapper>
      {showDeleteButton && !isLocked && (
        <TooltipWrapper tooltipTitle="Delete transition">
          <Button
            buttonStyle={ButtonStyle.TRANSPARENT}
            size={ButtonSize.SMALL}
            icon={<BenchlingIcon icon="delete" className={css(styles.actionButtonIcon)} />}
            onClick={() => onDeleteTransition?.(transitionId)}
            className={css(styles.actionButton)}
            dataTestAttrs={{
              component: 'FlowchartTransitionsSidepanelContent',
              element: 'deleteTransitionButton',
              key: transitionId,
            }}
          />
        </TooltipWrapper>
      )}
    </div>
  );
}

interface TransitionNodeColumnOptions {
  header: string;
  columnClassName: string;
  transitionDisplayInfos: Array<TransitionDisplayInfo>;
  getNodeName: (transition: TransitionDisplayInfo) => string;
  getNodeIcon: (transition: TransitionDisplayInfo) => BenchlingIconName | undefined;
}

/**
 * Render a column for a transition node.
 *
 * SimpleTable expects all children to be Column components, so using a function instead of making a new JSX.Element.
 */
function renderTransitionNodeColumn(options: TransitionNodeColumnOptions): JSX.Element {
  const {header, columnClassName, transitionDisplayInfos, getNodeName, getNodeIcon} = options;

  return (
    <Column
      header={<Cell className={columnClassName}>{header}</Cell>}
      renderCell={(rowIdx: number) => {
        const transition = transitionDisplayInfos[rowIdx];
        return (
          <Cell className={classnames(css(styles.wrapCellText), columnClassName)}>
            <Chip size={ChipSize.MINI} text={getNodeName(transition)} icon={getNodeIcon(transition)} />
          </Cell>
        );
      }}
    />
  );
}

interface TransitionsTableProps {
  transitionDisplayInfos: Array<TransitionDisplayInfo>;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
  showDeleteButton?: boolean;
  isLocked?: boolean;
}

function TransitionsTable(props: TransitionsTableProps): JSX.Element {
  const {
    transitionDisplayInfos,
    onViewOrEditTransition,
    onDeleteTransition,
    showDeleteButton = false,
    isLocked = false,
  } = props;

  return (
    <div className={css(styles.tableWrapper)}>
      <Table
        numRows={transitionDisplayInfos.length}
        calculateInView={false}
        fitContent={false}
        frozenHeader={false}
        getRowKey={(rowIndex) => transitionDisplayInfos[rowIndex].id}
        includeNegativeMargins={false}
        isCompact={false}
        overflowVisible={true}
        tableLayout="fixed"
      >
        {renderTransitionNodeColumn({
          header: 'From',
          columnClassName: css(styles.fromColumn),
          transitionDisplayInfos,
          getNodeName: (t) => t.fromNode.name,
          getNodeIcon: (t) => t.fromNode.icon,
        })}
        {renderTransitionNodeColumn({
          header: 'To',
          columnClassName: css(styles.toColumn),
          transitionDisplayInfos,
          getNodeName: (t) => t.toNode.name,
          getNodeIcon: (t) => t.toNode.icon,
        })}
        <Column
          header={<Cell className={css(styles.actionColumn)} />}
          renderCell={(rowIdx: number, options: CellRenderOptions) => {
            const transition = transitionDisplayInfos[rowIdx];
            return (
              <Cell className={css(styles.actionColumn)}>
                <TransitionRowActions
                  transitionId={transition.id}
                  isRowHovered={options.isRowHovered}
                  showDeleteButton={showDeleteButton}
                  isLocked={isLocked}
                  onViewOrEditTransition={onViewOrEditTransition}
                  onDeleteTransition={onDeleteTransition}
                />
              </Cell>
            );
          }}
        />
      </Table>
    </div>
  );
}

function EmptyTransitions({message, action}: {message: string; action?: JSX.Element}): JSX.Element {
  return (
    <div className={css(styles.emptyTransitions)}>
      <span className={css(styles.emptyTransitionsText)}>{message}</span>
      {action}
    </div>
  );
}

interface PlannedTransitionsSectionProps {
  transitionDisplayInfos: Array<TransitionDisplayInfo>;
  onViewOrEditTransition?: (transitionId: string) => void;
  onGoToFlowchart?: () => void;
  isLocked?: boolean;
}

function PlannedTransitionsSection(props: PlannedTransitionsSectionProps): JSX.Element {
  const {transitionDisplayInfos, onViewOrEditTransition, onGoToFlowchart, isLocked} = props;
  const hasTransitions = transitionDisplayInfos.length > 0;

  return (
    <div className={css(styles.section)}>
      <Accordion
        header={<div className={css(styles.title)}>Planned transitions</div>}
        headerSpacing="none"
        isOpenByDefault={true}
        dataTestAttrs={{
          component: 'FlowchartTransitionsSidepanelContent',
          element: 'plannedTransitionsAccordion',
        }}
      >
        {hasTransitions ? (
          <>
            <div className={css(styles.sectionDescription)}>
              Transitions drawn in the flowchart will appear here. You can edit these transitions, but they
              can't be deleted.
            </div>
            <TransitionsTable
              transitionDisplayInfos={transitionDisplayInfos}
              onViewOrEditTransition={onViewOrEditTransition}
              isLocked={isLocked}
            />
          </>
        ) : (
          <EmptyTransitions
            message={PLANNED_TRANSITIONS_EMPTY_STATE_MESSAGE}
            action={
              <Button
                buttonStyle={ButtonStyle.LINK}
                text={
                  <span className={css(styles.goToFlowchartLink)}>
                    Go to flowchart
                    <BenchlingIcon icon="arrow-right" className={css(styles.goToFlowchartArrow)} />
                  </span>
                }
                onClick={onGoToFlowchart}
                dataTestAttrs={{
                  component: 'FlowchartTransitionsSidepanelContent',
                  element: 'goToFlowchartButton',
                }}
              />
            }
          />
        )}
      </Accordion>
    </div>
  );
}

interface OptionalTransitionsSectionProps {
  transitionDisplayInfos: Array<TransitionDisplayInfo>;
  onViewOrEditTransition?: (transitionId: string) => void;
  onDeleteTransition?: (transitionId: string) => void;
  onAddTransition?: () => void;
  isLocked?: boolean;
}

function OptionalTransitionsSection(props: OptionalTransitionsSectionProps): JSX.Element {
  const {transitionDisplayInfos, onViewOrEditTransition, onDeleteTransition, onAddTransition, isLocked} =
    props;
  const hasTransitions = transitionDisplayInfos.length > 0;

  return (
    <div className={css(styles.section)}>
      <Accordion
        header={<div className={css(styles.title)}>Optional transitions</div>}
        isOpenByDefault={true}
        dataTestAttrs={{
          component: 'FlowchartTransitionsSidepanelContent',
          element: 'optionalTransitionsAccordion',
        }}
      >
        {hasTransitions ? (
          <>
            <div className={css(styles.sectionDescriptionWithButton)}>
              <span>
                Create transitions that users can draw while editing the flowchart, if editing is enabled.
              </span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.SMALL}
                icon="plus"
                text="Add optional transition"
                onClick={onAddTransition}
                disabled={isLocked}
                dataTestAttrs={{
                  component: 'FlowchartTransitionsSidepanelContent',
                  element: 'addOptionalTransitionButton',
                }}
              />
            </div>
            <TransitionsTable
              transitionDisplayInfos={transitionDisplayInfos}
              onViewOrEditTransition={onViewOrEditTransition}
              onDeleteTransition={onDeleteTransition}
              showDeleteButton={true}
              isLocked={isLocked}
            />
          </>
        ) : (
          <EmptyTransitions
            message={OPTIONAL_TRANSITIONS_EMPTY_STATE_MESSAGE}
            action={
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                icon="plus"
                text="Create optional transition"
                onClick={onAddTransition}
                disabled={isLocked}
                dataTestAttrs={{
                  component: 'FlowchartTransitionsSidepanelContent',
                  element: 'createOptionalTransitionButton',
                }}
              />
            }
          />
        )}
      </Accordion>
    </div>
  );
}

export default function FlowchartTransitionsSidepanelContent(
  props: FlowchartTransitionsSidepanelContentProps
): JSX.Element {
  const {
    metadataTransitions,
    ptdvIdToTaskSchemaId,
    taskDefinitionsForSidepanel,
    rootTaskSchema,
    onEditTransition,
    onCreateTransition,
    onDeleteTransition,
    onGoToFlowchart,
    isLocked,
  } = props;

  const transitionDisplayInfos = useMemo(
    () =>
      buildTransitionDisplayInfos(
        metadataTransitions,
        ptdvIdToTaskSchemaId,
        taskDefinitionsForSidepanel,
        rootTaskSchema
      ),
    [metadataTransitions, ptdvIdToTaskSchemaId, taskDefinitionsForSidepanel, rootTaskSchema]
  );

  const plannedTransitionDisplayInfos = useMemo(
    () => transitionDisplayInfos.filter((t) => t.isPlanned),
    [transitionDisplayInfos]
  );
  const optionalTransitionDisplayInfos = useMemo(
    () => transitionDisplayInfos.filter((t) => !t.isPlanned),
    [transitionDisplayInfos]
  );

  const handleViewOrEditPlannedTransition = useCallback(
    (transitionId: string) => {
      runModal(SingleFlowchartTransitionModal, {
        action: isLocked ? TransitionModalAction.VIEW : TransitionModalAction.EDIT,
        type: TransitionType.PLANNED,
        transitionId,
        onRequestSubmit: () => {
          if (isLocked) {
            return;
          }
          onEditTransition(transitionId);
        },
      });
    },
    [isLocked, onEditTransition]
  );

  const handleViewOrEditOptionalTransition = useCallback(
    (transitionId: string) => {
      runModal(SingleFlowchartTransitionModal, {
        action: isLocked ? TransitionModalAction.VIEW : TransitionModalAction.EDIT,
        type: TransitionType.OPTIONAL,
        transitionId,
        onRequestSubmit: () => {
          if (isLocked) {
            return;
          }
          onEditTransition(transitionId);
        },
      });
    },
    [isLocked, onEditTransition]
  );

  const handleAddOptionalTransition = useCallback(() => {
    runModal(SingleFlowchartTransitionModal, {
      action: TransitionModalAction.ADD,
      type: TransitionType.OPTIONAL,
      onRequestSubmit: onCreateTransition,
    });
  }, [onCreateTransition]);

  return (
    <div className={css(styles.container)}>
      <PlannedTransitionsSection
        transitionDisplayInfos={plannedTransitionDisplayInfos}
        onViewOrEditTransition={handleViewOrEditPlannedTransition}
        onGoToFlowchart={onGoToFlowchart}
        isLocked={isLocked}
      />
      <div className={css(styles.divider)} />
      <OptionalTransitionsSection
        transitionDisplayInfos={optionalTransitionDisplayInfos}
        onViewOrEditTransition={handleViewOrEditOptionalTransition}
        onDeleteTransition={onDeleteTransition}
        onAddTransition={handleAddOptionalTransition}
        isLocked={isLocked}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
  },
  title: {
    paddingLeft: '4px',
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
  },
  section: {
    marginBottom: '24px',
  },
  divider: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    marginLeft: '-16px',
    marginRight: '-16px',
    marginTop: 0,
    marginBottom: 0,
  },
  sectionDescription: {
    fontSize: '14px',
    color: Color.GRAY_900,
    marginTop: '8px',
    marginBottom: '16px',
  },
  sectionDescriptionWithButton: {
    fontSize: '14px',
    color: Color.GRAY_900,
    marginTop: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  fromColumn: {
    width: 'calc(50% - 60px)',
  },
  toColumn: {
    width: 'calc(50% - 60px)',
  },
  actionColumn: {
    width: '120px',
    textAlign: 'right',
  },
  wrapCellText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  tableWrapper: {
    overflow: 'hidden',
    ':global': {
      '.simpleTable': {
        overflowX: 'visible',
      },
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '0px',
    visibility: 'hidden',
    justifyContent: 'flex-end',
  },
  actionButton: {
    padding: '4px',
  },
  actionButtonIcon: {
    color: Color.GRAY_600,
  },
  actionButtonsVisible: {
    visibility: 'visible',
  },
  emptyTransitions: {
    backgroundColor: Color.GRAY_50,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 24,
    paddingBottom: 24,
    gap: '8px',
  },
  emptyTransitionsText: {
    maxWidth: '320px',
    textAlign: 'center',
    color: Color.GRAY_600,
    fontSize: '14px',
  },
  goToFlowchartLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  goToFlowchartArrow: {
    fontSize: '12px',
  },
});
